/**
 * Shared helpers for the FE-only mock data layer (see AdminApiService's
 * `MOCK:` sections). Every generator here is seeded off a stable input (a
 * store id, an index...) so reloading a page doesn't reshuffle the data the
 * reviewer is looking at.
 */

/** Small deterministic string hash → 32-bit int, used to seed a PRNG. */
export function hashSeed(input: string): number {
  let hash = 0
  for (let i = 0; i < input.length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i)
    hash |= 0
  }
  return hash >>> 0
}

/** Mulberry32 PRNG — tiny, deterministic, good enough for mock fixtures. */
export function seededRandom(seed: number): () => number {
  let a = seed
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function pick<T>(rng: () => number, list: T[]): T {
  return list[Math.floor(rng() * list.length)]
}

export function randomInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min
}

export function randomFloat(rng: () => number, min: number, max: number, decimals = 2): number {
  return Number((rng() * (max - min) + min).toFixed(decimals))
}

export function daysAgo(rng: () => number, maxDays: number): string {
  const d = new Date()
  d.setDate(d.getDate() - randomInt(rng, 0, maxDays))
  return d.toISOString()
}

export function paginate<T>(
  list: T[],
  page: number,
  limit: number,
): { results: T[]; totalResults: number; currentPage: number; limit: number; lastPage: number } {
  const totalResults = list.length
  const lastPage = Math.max(1, Math.ceil(totalResults / limit))
  const currentPage = Math.min(Math.max(1, page), lastPage)
  const start = (currentPage - 1) * limit
  return { results: list.slice(start, start + limit), totalResults, currentPage, limit, lastPage }
}
